import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Chip,
  LinearProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { testRunsApi, agentsApi, questionBasesApi } from '../services/api';

function TestRuns() {
  const navigate = useNavigate();
  const [testRuns, setTestRuns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [questionBases, setQuestionBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    agent_id: '',
    question_base_ids: [],
    parallel_questions: 1,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
    // Poll for updates every 5 seconds
    const interval = setInterval(loadTestRuns, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [testsRes, agentsRes, basesRes] = await Promise.all([
        testRunsApi.list(),
        agentsApi.list(),
        questionBasesApi.list(),
      ]);
      setTestRuns(testsRes.data);
      setAgents(agentsRes.data);
      setQuestionBases(basesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestRuns = async () => {
    try {
      const response = await testRunsApi.list();
      setTestRuns(response.data);
    } catch (error) {
      console.error('Error loading test runs:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.agent_id || formData.question_base_ids.length === 0) return;
    
    setSaving(true);
    try {
      await testRunsApi.create({
        name: formData.name,
        agent_id: parseInt(formData.agent_id),
        question_base_ids: formData.question_base_ids.map(id => parseInt(id)),
        parallel_questions: parseInt(formData.parallel_questions),
      });
      setDialogOpen(false);
      setFormData({ name: '', agent_id: '', question_base_ids: [], parallel_questions: 1 });
      loadTestRuns();
    } catch (error) {
      console.error('Error creating test run:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir este teste?')) return;
    
    try {
      await testRunsApi.delete(id);
      loadTestRuns();
    } catch (error) {
      console.error('Error deleting test run:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'running':
        return <RefreshIcon color="warning" sx={{ animation: 'spin 1s linear infinite' }} />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'info';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const basesWithQuestions = questionBases.filter((b) => b.questions_count > 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Testes de Avaliação</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTestRuns}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={agents.length === 0 || basesWithQuestions.length === 0}
          >
            Novo Teste
          </Button>
        </Box>
      </Box>

      {agents.length === 0 || basesWithQuestions.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <PlayIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Configuração necessária
              </Typography>
              <Typography color="textSecondary" mb={2}>
                {agents.length === 0 && 'Cadastre pelo menos um agente. '}
                {basesWithQuestions.length === 0 && 'Crie uma base de perguntas com perguntas geradas.'}
              </Typography>
              <Box display="flex" gap={2} justifyContent="center">
                {agents.length === 0 && (
                  <Button variant="outlined" onClick={() => navigate('/agents')}>
                    Cadastrar Agente
                  </Button>
                )}
                {basesWithQuestions.length === 0 && (
                  <Button variant="outlined" onClick={() => navigate('/question-bases')}>
                    Criar Base de Perguntas
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : testRuns.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <PlayIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Nenhum teste realizado
              </Typography>
              <Typography color="textSecondary" mb={2}>
                Crie um novo teste para avaliar seu agente
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Criar Primeiro Teste
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {testRuns.map((test) => (
            <Grid item xs={12} key={test.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                }}
                onClick={() => navigate(`/test-runs/${test.id}`)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                      {getStatusIcon(test.status)}
                      <Box>
                        <Typography variant="h6">{test.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Agente: {test.agent_name} | Bases: {test.question_base_names?.join(', ') || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={test.status}
                        size="small"
                        color={getStatusColor(test.status)}
                      />
                      {test.overall_score !== null && (
                        <Chip
                          label={`${test.overall_score.toFixed(1)}%`}
                          color={getScoreColor(test.overall_score)}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {test.status === 'running' && (
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="textSecondary">
                          Progresso
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {test.completed_questions}/{test.total_questions}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(test.completed_questions / test.total_questions) * 100}
                      />
                    </Box>
                  )}
                  
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      {test.total_questions} perguntas
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(test.created_at).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/test-runs/${test.id}`)}>
                    Ver Detalhes
                  </Button>
                  {test.status === 'completed' && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/test-runs/${test.id}`);
                      }}
                    >
                      Ver Relatório
                    </Button>
                  )}
                  <Box flexGrow={1} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(test.id, e)}
                    disabled={test.status === 'running'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Teste de Avaliação</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Teste"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Agente</InputLabel>
            <Select
              value={formData.agent_id}
              label="Agente"
              onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Bases de Perguntas</InputLabel>
            <Select
              multiple
              value={formData.question_base_ids}
              onChange={(e) => setFormData({ ...formData, question_base_ids: e.target.value })}
              input={<OutlinedInput label="Bases de Perguntas" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const qb = basesWithQuestions.find((b) => b.id === id);
                    return qb ? qb.name : id;
                  })
                  .join(', ')
              }
            >
              {basesWithQuestions.map((base) => (
                <MenuItem key={base.id} value={base.id}>
                  <Checkbox checked={formData.question_base_ids.indexOf(base.id) > -1} />
                  <ListItemText
                    primary={base.name}
                    secondary={`${base.questions_count} perguntas`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Perguntas em Paralelo"
            type="number"
            fullWidth
            value={formData.parallel_questions}
            onChange={(e) =>
              setFormData({
                ...formData,
                parallel_questions: Math.min(20, Math.max(1, parseInt(e.target.value) || 1)),
              })
            }
            inputProps={{ min: 1, max: 20 }}
            helperText="Número de perguntas a serem processadas simultaneamente (1-20)"
          />
          {basesWithQuestions.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Nenhuma base de perguntas com perguntas disponível.
              Gere perguntas sintéticas primeiro.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              !formData.name.trim() ||
              !formData.agent_id ||
              formData.question_base_ids.length === 0 ||
              saving
            }
          >
            {saving ? <CircularProgress size={24} /> : 'Iniciar Teste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TestRuns;
